import { formatCompactINR, formatINR, formatNum, exportCSV } from "../../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Layers, Download } from "lucide-react";
import { Button } from "../ui/button";

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1E293B] border border-white/10 rounded-lg p-3 text-xs">
      <p className="font-medium text-white mb-1">{d.name}</p>
      <p className="text-slate-400">Spend: <span className="text-white">{formatCompactINR(d.spend)}</span></p>
      <p className="text-slate-400">ROAS: <span className="text-[#10B981]">{d.roas}x</span></p>
    </div>
  );
}

export default function ServiceMatrix({ matrix }) {
  if (!matrix) return null;

  return (
    <div data-testid="service-matrix" className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">Service-Wise Spend & ROI</h2>
        </div>
        <Button data-testid="export-services-csv" onClick={() => exportCSV(matrix, "service_roi.csv")}
          className="h-8 bg-[#0F172A] hover:bg-[#334155] border border-white/10 text-slate-300 rounded-full text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
        </Button>
      </div>

      <div className="h-44 mb-5">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={matrix} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.split(" ")[0]}
              axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => `${(v / 1e5).toFixed(0)}L`} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="spend" radius={[6, 6, 0, 0]}>
              {matrix.map((s, i) => <Cell key={i} fill={s.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left font-medium py-2 px-2">Service Line</th>
              <th className="text-right font-medium py-2 px-2">Spend</th>
              <th className="text-right font-medium py-2 px-2">Leads</th>
              <th className="text-right font-medium py-2 px-2">CPL</th>
              <th className="text-right font-medium py-2 px-2">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((s, i) => {
              const cplOver = s.cpl > s.target_cpl;
              return (
                <tr key={i} className="border-b border-white/5 hover:bg-[#0F172A]/50 transition-colors">
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-slate-200">{s.name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-right tabular text-slate-200">{formatCompactINR(s.spend)}</td>
                  <td className="py-2.5 px-2 text-right tabular text-slate-200">{formatNum(s.leads)}</td>
                  <td className="py-2.5 px-2 text-right tabular">
                    <span style={{ color: cplOver ? "#EF4444" : "#10B981" }}>{formatINR(s.cpl)}</span>
                    <span className="text-slate-500 text-xs ml-1">/{formatINR(s.target_cpl)}</span>
                  </td>
                  <td className="py-2.5 px-2 text-right tabular font-medium text-[#10B981]">{s.roas}x</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
